import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, MaskLinesInView } from "./motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQS = [
  { q: "Qual a capacidade máxima da casa?", a: "A Casa Montebello acomoda confortavelmente até 14 hóspedes, distribuídos por vários quartos. Para grupos maiores, contacte-nos diretamente para discutirmos disponibilidade." },
  { q: "Como funciona o check-in e o check-out?", a: "O check-in é feito a partir das 15h e o check-out até às 11h. Combinamos consigo os detalhes de chegada e entrega de chaves nos dias que antecedem a estadia." },
  { q: "Podemos cozinhar para o grupo?", a: "Sim, a cozinha está totalmente equipada para receber grupos grandes — com forno, placa, frigorífico amplo, loiça e utensílios para várias refeições. Ideal para longos convívios em família." },
  { q: "Aceitam animais de estimação?", a: "Sim, aceitamos animais de estimação bem comportados mediante aviso prévio. Contacte-nos para confirmar detalhes." },
  { q: "Quais os pontos turísticos próximos?", a: "Estamos a 40 minutos do Porto, perto do Douro Vinhateiro, dos santuários de Felgueiras e das serras. Uma base perfeita para explorar o norte de Portugal." },
];

export default function LocationContact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Preencha nome, email e mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Mensagem enviada! Entraremos em contacto brevemente.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar a mensagem.";
      toast.error(typeof msg === "string" ? msg : "Erro ao enviar mensagem.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contactos" data-testid="contact-section" className="relative py-28 lg:py-40 bg-cream">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left: location */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-14 bg-forest/40" />
                <p className="overline">Localização & Contactos</p>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.05] tracking-tight text-forest font-light">
              <MaskLinesInView lines={["Encontre-nos em", <span key="i" className="italic">Felgueiras.</span>]} />
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-8 text-muted-foreground text-lg leading-relaxed max-w-md">
                A 40 minutos do Porto, no coração do Vale do Sousa. Perto do Douro, dos vinhos verdes e das serras. Longe do ruído.
              </p>

              <div className="mt-10 space-y-5">
                <div className="flex gap-4">
                  <MapPin size={20} strokeWidth={1.5} className="text-terracotta mt-1 shrink-0" />
                  <div>
                    <div className="text-forest font-medium">Casa Montebello</div>
                    <div className="text-muted-foreground text-sm">Felgueiras, Porto · Portugal</div>
                    <a href="https://maps.app.goo.gl/mPYSjJTfhJ2F8bKY8" target="_blank" rel="noopener noreferrer" data-testid="map-directions-link" className="mt-2 inline-block text-xs tracking-wider uppercase text-terracotta hover:underline">
                      Ver direções no Google Maps →
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Mail size={20} strokeWidth={1.5} className="text-terracotta mt-1 shrink-0" />
                  <div>
                    <a href="mailto:casamontebello.pt@gmail.com" data-testid="email-link" className="text-forest font-medium hover:text-terracotta transition-colors">casamontebello.pt@gmail.com</a>
                    <div className="text-muted-foreground text-sm">Escreva-nos — respondemos com gosto</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock size={20} strokeWidth={1.5} className="text-terracotta mt-1 shrink-0" />
                  <div>
                    <div className="text-forest font-medium">Check-in / Check-out</div>
                    <div className="text-muted-foreground text-sm">Entrada 15h · Saída 11h</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 aspect-[4/3] w-full rounded-sm overflow-hidden border border-border">
                <iframe title="Mapa Casa Montebello" data-testid="location-map" src="https://www.google.com/maps?q=41.327156,-8.229328&z=15&output=embed" width="100%" height="100%" loading="lazy" style={{ border: 0, filter: "saturate(0.85) contrast(1.05)" }} referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </Reveal>
          </div>

          {/* Right: inquiry + FAQ */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-border p-8 lg:p-12 rounded-sm">
                <p className="overline mb-6">Perguntas & Mensagens</p>
                <h3 className="font-display text-3xl lg:text-4xl text-forest leading-tight">Tem alguma dúvida?</h3>
                <p className="mt-3 text-muted-foreground">Para reservas use o calendário acima. Para questões, escreva-nos aqui.</p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Nome *">
                    <input data-testid="contact-name-input" name="name" value={form.name} onChange={onChange} required className="input-line" placeholder="O seu nome" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="contact-email-input" type="email" name="email" value={form.email} onChange={onChange} required className="input-line" placeholder="voce@email.com" />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Telefone">
                    <input data-testid="contact-phone-input" name="phone" value={form.phone} onChange={onChange} className="input-line" placeholder="+351 ..." />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Mensagem *">
                    <textarea data-testid="contact-message-input" name="message" value={form.message} onChange={onChange} required rows={5} className="input-line resize-none" placeholder="A sua questão..." />
                  </Field>
                </div>

                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="mt-8 group inline-flex items-center gap-3 h-14 pl-8 pr-6 rounded-full bg-forest text-cream font-medium text-[0.82rem] tracking-[0.2em] uppercase hover:bg-[#1f2f21] transition-all disabled:opacity-60">
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> A enviar...</>) : (<>Enviar Mensagem <span className="flex items-center justify-center w-9 h-9 rounded-full bg-cream text-forest group-hover:translate-x-1 transition-transform"><ArrowRight size={16} strokeWidth={1.75} /></span></>)}
                </button>
              </form>
            </Reveal>

            <div className="mt-14">
              <Reveal><p className="overline mb-4">Perguntas Frequentes</p></Reveal>
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((f, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`} className="border-b border-border/70">
                    <AccordionTrigger data-testid={`faq-trigger-${idx}`} className="font-display text-xl lg:text-2xl text-forest hover:no-underline text-left py-6">{f.q}</AccordionTrigger>
                    <AccordionContent data-testid={`faq-content-${idx}`} className="text-muted-foreground text-base leading-relaxed pb-6">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .input-line { width:100%; background:transparent; border:none; border-bottom:1px solid hsl(var(--border)); padding:10px 0; font-family:'Manrope',sans-serif; font-size:0.95rem; color:hsl(var(--foreground)); transition:border-color .2s ease; outline:none; }
        .input-line::placeholder { color:hsl(var(--muted-foreground) / 0.6); }
        .input-line:focus { border-bottom-color:hsl(var(--primary)); }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
